import { useEffect } from "react";
import { useAdminBranding } from "@/hooks/queries";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";

// Aplica a identidade visual configurada em Administração > Identidade
// Visual > Tema & Cor / Logotipos no app inteiro, uma vez por sessão.
// Montado uma única vez em __root.tsx (só quando autenticado — reaproveita
// o mesmo useAdminBranding() que a sidebar usa pro logotipo, React Query
// deduplica). `--primary` é uma custom property CSS comum (ver
// styles.css) — sobrescrever com um hex funciona mesmo o valor default
// sendo oklch(), CSS não exige que os dois usem o mesmo espaço de cor.
//
// Sobrescrever só `--primary` não bastava: a sidebar (o elemento mais
// visível da marca, sempre na tela) usa um conjunto de tokens PRÓPRIO
// (--sidebar-primary/--sidebar-ring, ver styles.css) que por padrão tem o
// mesmo valor de --primary mas é uma variável CSS totalmente separada —
// mudar só --primary deixava a sidebar sempre azul, dando a impressão de
// "a cor não muda" mesmo com o valor salvo corretamente no backend. --ring
// (foco de inputs/botões em geral) tinha o mesmo problema.
//
// --accent/--sidebar-accent tinham o MESMO problema, só que mais visível
// ainda: é o token usado pro hover/estado ativo de item de menu, item de
// Select/DropdownMenu/Command e o item ativo da própria sidebar (ver
// data-[active=true]:bg-sidebar-accent em ui/sidebar.tsx) — ou seja, a
// maior parte do que o usuário efetivamente vê pintado ao passar o mouse
// ou navegar continuava azul mesmo com a cor da empresa salva (bug real
// 2026-08-28: "boto es continuam na cor errada" mesmo o Salvar funcionando
// e a cor persistindo certo no banco). color-mix() com "transparent" (em
// vez de um hex sólido calculado) deixa o resultado certo tanto no tema
// claro quanto escuro sem precisar saber qual dos dois está ativo — a
// transparência se mistura com o fundo (claro ou escuro) por baixo.
// Chave lida (antes de qualquer React montar) pelo script inline em
// __root.tsx — mesma cor, aplicada de forma síncrona/bloqueante assim que
// o <head> carrega, pra never mostrar o azul padrão antes da cor real
// (bug relatado 2026-08-28: F5/entrar no sistema mostrava azul por um
// instante e só depois a cor da empresa). Este hook continua sendo a
// fonte de verdade (busca no backend) e apenas atualiza o cache pro
// próximo carregamento; ele não substitui o script inline, os dois juntam
// pra cobrir "primeira visita" (sem cache ainda, inevitável) e "visitas
// seguintes" (cache já aplicado antes do paint).
const BRANDING_COLOR_CACHE_KEY = "gpsdrive:branding-color";

export function useBrandingTheme(): void {
  const { data } = useAdminBranding();

  useEffect(() => {
    if (!data?.primaryColor) return;
    const root = document.documentElement.style;
    const color = data.primaryColor;
    const accent = `color-mix(in srgb, ${color} 15%, transparent)`;
    const sidebarAccent = `color-mix(in srgb, ${color} 18%, transparent)`;
    root.setProperty("--primary", color);
    root.setProperty("--ring", color);
    root.setProperty("--sidebar-primary", color);
    root.setProperty("--sidebar-ring", color);
    root.setProperty("--accent", accent);
    root.setProperty("--accent-foreground", color);
    root.setProperty("--sidebar-accent", sidebarAccent);
    root.setProperty("--sidebar-accent-foreground", color);
    try {
      localStorage.setItem(BRANDING_COLOR_CACHE_KEY, color);
    } catch {
      // localStorage indisponível — sem cache, próximo F5 só pisca uma vez
    }
    return () => {
      root.removeProperty("--primary");
      root.removeProperty("--ring");
      root.removeProperty("--sidebar-primary");
      root.removeProperty("--sidebar-ring");
      root.removeProperty("--accent");
      root.removeProperty("--accent-foreground");
      root.removeProperty("--sidebar-accent");
      root.removeProperty("--sidebar-accent-foreground");
    };
  }, [data?.primaryColor]);

  useEffect(() => {
    // Logotipo do aplicativo: favicon dedicado, senão o logotipo principal.
    const raw = data?.faviconUrl ?? data?.logoUrl;
    if (!raw) return;
    const href = resolveGpsDriveUrl(raw);
    applyAppIcon(href);
    try {
      localStorage.setItem(APP_ICON_CACHE_KEY, href);
    } catch {
      // sem cache, só a primeira tela depois do F5 mostra o ícone antigo
    }
  }, [data?.faviconUrl, data?.logoUrl]);
}

const APP_ICON_CACHE_KEY = "gpsdrive:app-icon";

// Troca favicon, apple-touch-icon e o ícone do manifest (instalação do PWA)
// pelo logotipo configurado — antes eram fixos no index e só o favicon mudava,
// e só depois do login.
function applyAppIcon(href: string): void {
  if (typeof document === "undefined") return;
  const setLink = (rel: string) => {
    let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.removeAttribute("type");
    link.href = href;
  };
  setLink("icon");
  setLink("apple-touch-icon");
  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifestLink) {
    const manifest = {
      name: "GPS Drive",
      short_name: "GPS Drive",
      start_url: `${location.origin}/`,
      scope: `${location.origin}/`,
      display: "standalone",
      background_color: "#0b0f14",
      theme_color: "#0b0f14",
      icons: [
        { src: href, sizes: "192x192", purpose: "any" },
        { src: href, sizes: "512x512", purpose: "any" },
      ],
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" }),
    );
    manifestLink.href = url;
  }
}

// Aplica o último ícone conhecido logo no boot (inclusive na tela de login,
// antes de qualquer requisição autenticada).
export function applyCachedAppIcon(): void {
  try {
    const href = localStorage.getItem(APP_ICON_CACHE_KEY);
    if (href) applyAppIcon(href);
  } catch {
    // ignore
  }
}
